/**
 * Wind Field Controls (Alpine.data)
 *
 * Drives the FAB + collapsible modal that lives in
 * `views/components/interactive/wind-field-controls.antlers.html`.
 *
 * Binds to `window.__windField` once the shader system has booted (the app
 * dispatches `wind-field:ready`). When unavailable (probe failed, WebGL off,
 * reduced-motion overriding, etc.) the component stays disabled — the
 * template just renders an inert FAB with no panel.
 */

export default function windFieldControls() {
    return {
        ready: false,
        open: false,
        speed: 0.3,
        values: /** @type {Record<string, number>} */ ({}),
        params: /** @type {Array<{key:string,label:string,min:number,max:number,step:number,default:number}>} */ ([]),
        presets: /** @type {Array<{label:string,values:Record<string, number>}>} */ ([]),
        fps: 0,
        _fpsTimer: 0,
        _onReady: null,

        init() {
            this._hydrate();
            if (!this.ready) {
                // Re-try once the shader system finishes booting.
                this._onReady = () => this._hydrate();
                window.addEventListener('wind-field:ready', this._onReady, { once: true });
            }
        },

        destroy() {
            if (this._onReady) {
                window.removeEventListener('wind-field:ready', this._onReady);
            }
            if (this._fpsTimer) clearInterval(this._fpsTimer);
        },

        _hydrate() {
            const state = window.__windField;
            if (!state) return;

            this.params = state.params;
            this.presets = state.presets;
            this.speed = state.baseSpeedDefault ?? 0.3;

            const fresh = {};
            for (const p of this.params) fresh[p.key] = p.default;
            this.values = fresh;

            // Seed the effect with matching values so the first frame matches the UI.
            state.effect.setSpeed(this.speed);
            state.effect.setParams(fresh);

            // Poll FPS from the effect instance (cheap — the effect tracks it itself).
            this._fpsTimer = setInterval(() => {
                this.fps = state.effect.fps || 0;
            }, 500);

            this.ready = true;
        },

        toggle() {
            this.open = !this.open;
        },

        close() {
            this.open = false;
        },

        setSpeed(value) {
            const n = parseFloat(value);
            this.speed = n;
            window.__windField?.effect.setSpeed(n);
        },

        setParam(key, value) {
            const n = parseFloat(value);
            this.values = { ...this.values, [key]: n };
            window.__windField?.effect.setParam(key, n);
        },

        applyPreset(preset) {
            const merged = { ...this.values, ...preset.values };
            this.values = merged;
            window.__windField?.effect.setParams(preset.values);
        },

        /** Precision for the slider value readout. Follows the POC's rule. */
        formatValue(step, value) {
            if (step < 0.001) return value.toFixed(4);
            if (step < 0.01) return value.toFixed(3);
            if (step < 1) return value.toFixed(2);
            return String(Math.round(value));
        },

        // --- CSP-safe accessors ---
        // Alpine's CSP build (@alpinejs/csp) only allows property access, method
        // calls, logical operators, and ternaries — no arithmetic, no ??, no
        // string concatenation. These helpers keep template expressions within
        // that subset.

        /** Current value for a param, falling back to its default. */
        paramValue(p) {
            const v = this.values[p.key];
            return v === undefined ? p.default : v;
        },

        /** Formatted value-readout for a slider. */
        paramReadout(p) {
            return this.formatValue(p.step, this.paramValue(p));
        },

        /** "60 FPS" text for the heading badge. */
        fpsLabel() {
            return this.fps + ' FPS';
        },

        /** "2.50" text for the speed slider readout. */
        speedReadout() {
            return this.speed.toFixed(2);
        },

        /** Build the per-param slider id. */
        paramInputId(p) {
            return 'wind-field-' + p.key;
        },
    };
}
